// Sidebar.jsx (optimized version using AuthContext)
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FiMenu,
  FiGrid,
  FiMail,
  FiCalendar,
  FiMap,
  FiBarChart2,
  FiLogOut,
} from "react-icons/fi";
import { useAuth } from "../../providers/AuthContext";
import { BiLoaderAlt } from "react-icons/bi";

// Default user data as fallback
const DEFAULT_USER = {
  name: "User Name",
  email: "user@example.com",
  picture: "/default-avatar.svg",
};

const Sidebar = ({ isOpen, onClose, onCollapse }) => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, logoutLoading } = useAuth();
  console.log("Logout loading is", logoutLoading);

  // Set active tab based on current route
  useEffect(() => {
    const path = location.pathname;
    if (path === "/" || path === "/dashboard") {
      setActiveTab("dashboard");
    } else if (path.includes("/email")) {
      setActiveTab("email");
    } else if (path.includes("/calendar")) {
      setActiveTab("calendar");
    } else if (path.includes("/travel")) {
      setActiveTab("travel");
    } else if (path.includes("/analytics")) {
      setActiveTab("analytics");
    } else if (path.includes("/settings")) {
      setActiveTab("settings");
    }
  }, [location.pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const getButtonClass = (tabName) => {
    const baseClass =
      "flex items-center space-x-3 px-3 py-2 rounded-md transition-colors duration-200";
    return `${baseClass} ${
      activeTab === tabName
        ? "bg-[#1F9BFF1A] text-[#1F9BFF]"
        : "bg-white text-gray-600 hover:bg-[#1F9BFF1A] hover:text-[#1F9BFF]"
    }`;
  };

  const navigateTo = (path, tab) => {
    setActiveTab(tab);
    navigate(path);
    if (onClose) onClose();
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
    if (onCollapse) onCollapse(!isCollapsed);
  };

  // Get user information from context
  const userName = user?.name || DEFAULT_USER.name;
  const userEmail = user?.email || DEFAULT_USER.email;
  const userPicture = user?.avatar_url || DEFAULT_USER.picture;

  return (
    <>
      <div
        className={`fixed top-0 left-0 h-screen flex flex-col justify-between bg-white transition-all duration-300 ${
          isCollapsed ? "w-[70px]" : "w-[18%]"
        } overflow-x-hidden z-50`}
      >
        <div
          className={`flex ${
            isCollapsed ? "justify-center" : "justify-between"
          } items-center p-5`}
        >
          <button
            onClick={toggleSidebar}
            className="p-2 hover:bg-blue-50 rounded-full transition-colors duration-200"
          >
            <FiMenu className="h-6 w-6 text-[#1F9BFF] bg-[#1F9BFF33] rounded-full p-1" />
          </button>
          {!isCollapsed && (
            <img src="/Efficia-01.svg" alt="Logo" className="h-8 mx-auto" />
          )}
        </div>

        <div
          className={`flex flex-col flex-1 space-y-4 p-4 overflow-y-auto shadow-lg shadow-gray-300 ${
            isCollapsed ? "items-center overflow-x-hidden" : ""
          }`}
        >
          <button
            onClick={() => navigateTo("/dashboard", "dashboard")}
            className={getButtonClass("dashboard")}
          >
            <FiGrid className="h-5 w-5" />
            {!isCollapsed && <span>Dashboard</span>}
          </button>
          <button
            onClick={() => navigateTo("/email", "email")}
            className={getButtonClass("email")}
          >
            <FiMail className="h-5 w-5" />
            {!isCollapsed && <span>Email</span>}
          </button>
          <button
            onClick={() => navigateTo("/calendar", "calendar")}
            className={getButtonClass("calendar")}
          >
            <FiCalendar className="h-5 w-5" />
            {!isCollapsed && <span>Calendar</span>}
          </button>
          {/* <button
            onClick={() => navigateTo("/travel", "travel")}
            className={getButtonClass("travel")}
          >
            <FiMap className="h-5 w-5" />
            {!isCollapsed && <span>Travel</span>}
          </button> */}
          <button
            onClick={() => navigateTo("/analytics", "analytics")}
            className={getButtonClass("analytics")}
          >
            <FiBarChart2 className="h-5 w-5" />
            {!isCollapsed && <span>Analytics</span>}
          </button>
        </div>

        <div className="sticky bottom-0 p-4 border-t border-gray-200 bg-white">
          {isCollapsed ? (
            <button
              onClick={handleLogout}
              className="p-2 hover:bg-blue-50 rounded-full transition-colors duration-200"
            >
              <FiLogOut className="h-6 w-6 text-[#1F9BFF]" />
            </button>
          ) : (
            <div className="relative bg-[#001D47] p-4 px-6 rounded-md w-full mx-auto flex items-start space-x-2 overflow-hidden">
              <div className="absolute bg-gradient-to-r from-[#00419E] to-[#001D47] w-[100px] h-[100px] bottom-[-20%] right-[-10%] z-0 rounded-full"></div>
              <img
                src={userPicture}
                alt="Profile"
                className="h-8 w-8 rounded-full"
                referrerPolicy="no-referrer"
                crossOrigin="anonymous"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = DEFAULT_USER.picture;
                }}
              />
              <div className="text-white flex-1 overflow-hidden relative z-10">
                <div className="font-semibold text-sm truncate max-w-[120px]">
                  {userName}
                </div>
                <div className="text-xs truncate max-w-[120px]">
                  {userEmail}
                </div>

                <button
                  onClick={handleLogout}
                  disabled={logoutLoading}
                  className="flex items-center justify-center px-4 py-2 mt-2 text-sm font-medium text-white transition-all duration-300 bg-[#1F9BFF] rounded-md shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-blue-500 disabled:opacity-70 disabled:cursor-not-allowed group"
                >
                  {logoutLoading ? (
                    <BiLoaderAlt className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FiLogOut className="w-4 h-4 mr-2 transition-transform duration-200 group-hover:-translate-x-1" />
                  )}
                  <span
                    className={`transition-all duration-200 ${
                      logoutLoading ? "text-xs" : "text-sm"
                    }`}
                  >
                    {logoutLoading ? "Logging out..." : "Logout"}
                  </span>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default React.memo(Sidebar);
